import NumbersIcon from "@mui/icons-material/Numbers";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import StraightenIcon from "@mui/icons-material/Straighten";
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tooltip,
} from "@mui/material";
import { useCallback, useState, type FunctionComponent } from "react";
import {
  decimalPlacesOptions,
  getDecimalPlacesLabel,
  type DecimalPlaces,
} from "../features/NumberFormatter";

interface SettingsMenuProps {
  isGridVisible: boolean;
  isSnapToGrid: boolean;
  isTooltipsVisible: boolean;
  decimalPlaces: DecimalPlaces;
  isNodeValuesVisible: boolean;
  isNodeDerivativesVisible: boolean;
  isLocalDerivativesVisible: boolean;
  isTopologicalOrderVisible: boolean;
  isGlobalNodeInfluence: boolean;
  areAnswersShown: boolean;
  onToggleGrid: () => void;
  onToggleSnapToGrid: () => void;
  onToggleTooltips: () => void;
  onDecimalPlacesChange: (decimalPlaces: DecimalPlaces) => void;
  onToggleNodeValues: () => void;
  onToggleNodeDerivatives: () => void;
  onToggleLocalDerivatives: () => void;
  onToggleTopologicalOrder: () => void;
  onToggleGlobalNodeInfluence: () => void;
  onToggleAnswers: () => void;
}

const SettingsMenu: FunctionComponent<SettingsMenuProps> = ({
  isGridVisible,
  isSnapToGrid,
  isTooltipsVisible,
  decimalPlaces,
  isNodeValuesVisible,
  isNodeDerivativesVisible,
  isLocalDerivativesVisible,
  isTopologicalOrderVisible,
  isGlobalNodeInfluence,
  areAnswersShown,
  onToggleGrid,
  onToggleSnapToGrid,
  onToggleTooltips,
  onDecimalPlacesChange,
  onToggleNodeValues,
  onToggleNodeDerivatives,
  onToggleLocalDerivatives,
  onToggleTopologicalOrder,
  onToggleGlobalNodeInfluence,
  onToggleAnswers,
}) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const handleClose = useCallback(() => {
    setAnchorElement(null);
  }, []);

  return (
    <>
      <Tooltip title="Settings">
        <IconButton
          aria-label="Settings"
          color="inherit"
          onClick={(event) => {
            setAnchorElement(event.currentTarget);
          }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>

      <Menu
        open={anchorElement !== null}
        anchorEl={anchorElement}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true, "aria-label": "settings-menu" }}
        slotProps={{ paper: { sx: { minWidth: 248 } } }}
      >
        <ListSubheader disableSticky>Canvas</ListSubheader>

        <MenuItem onClick={onToggleGrid}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Show grid</ListItemText>
          <Switch
            size="small"
            checked={isGridVisible}
            inputProps={{ "aria-label": "Show grid" }}
            // The whole row already toggles it, so the switch must not toggle
            // it a second time
            onClick={(event) => {
              event.stopPropagation();
              onToggleGrid();
            }}
          />
        </MenuItem>

        <MenuItem onClick={onToggleSnapToGrid}>
          <ListItemIcon>
            <StraightenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Snap to grid</ListItemText>
          <Switch
            size="small"
            checked={isSnapToGrid}
            inputProps={{ "aria-label": "Snap to grid" }}
            onClick={(event) => {
              event.stopPropagation();
              onToggleSnapToGrid();
            }}
          />
        </MenuItem>

        <MenuItem onClick={onToggleTooltips}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Show tooltips</ListItemText>
          <Switch
            size="small"
            checked={isTooltipsVisible}
            inputProps={{ "aria-label": "Show tooltips" }}
            onClick={(event) => {
              event.stopPropagation();
              onToggleTooltips();
            }}
          />
        </MenuItem>

        <Divider />

        <ListSubheader disableSticky>Values</ListSubheader>

        {/* Not a MenuItem: the row holds a Select, and a click meant for the
        dropdown must not also count as picking the row */}
        <li>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 2, py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <NumbersIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Decimal places</ListItemText>
            <Select
              // A real <select>: the menu is already a popover, and stacking
              // MUI's own popover on top of it fights for the click and the
              // focus
              native
              size="small"
              variant="standard"
              value={decimalPlaces === null ? "auto" : `${decimalPlaces}`}
              inputProps={{ "aria-label": "Decimal places" }}
              onChange={(event) => {
                const value = event.target.value;
                onDecimalPlacesChange(value === "auto" ? null : Number(value));
              }}
            >
              {decimalPlacesOptions.map((option) => (
                <option
                  key={option === null ? "auto" : option}
                  value={option === null ? "auto" : `${option}`}
                >
                  {getDecimalPlacesLabel(option)}
                </option>
              ))}
            </Select>
          </Stack>
        </li>

        <MenuItem onClick={onToggleNodeValues}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Show node values</ListItemText>
          <Switch
            size="small"
            checked={isNodeValuesVisible}
            inputProps={{ "aria-label": "Show node values" }}
            onClick={(event) => {
              event.stopPropagation();
              onToggleNodeValues();
            }}
          />
        </MenuItem>

        <MenuItem onClick={onToggleNodeDerivatives}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Show node derivatives</ListItemText>
          <Switch
            size="small"
            checked={isNodeDerivativesVisible}
            inputProps={{ "aria-label": "Show node derivatives" }}
            onClick={(event) => {
              event.stopPropagation();
              onToggleNodeDerivatives();
            }}
          />
        </MenuItem>

        <MenuItem onClick={onToggleLocalDerivatives}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Show local derivatives</ListItemText>
          <Switch
            size="small"
            checked={isLocalDerivativesVisible}
            inputProps={{ "aria-label": "Show local derivatives" }}
            onClick={(event) => {
              event.stopPropagation();
              onToggleLocalDerivatives();
            }}
          />
        </MenuItem>

        <MenuItem onClick={onToggleTopologicalOrder}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Show topological order</ListItemText>
          <Switch
            size="small"
            checked={isTopologicalOrderVisible}
            inputProps={{ "aria-label": "Show topological order" }}
            onClick={(event) => {
              event.stopPropagation();
              onToggleTopologicalOrder();
            }}
          />
        </MenuItem>

        <MenuItem onClick={onToggleGlobalNodeInfluence}>
          <ListItemIcon>
            <StraightenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Global node influence</ListItemText>
          <Switch
            size="small"
            checked={isGlobalNodeInfluence}
            inputProps={{ "aria-label": "Global node influence" }}
            onClick={(event) => {
              event.stopPropagation();
              onToggleGlobalNodeInfluence();
            }}
          />
        </MenuItem>

        <Divider />

        <ListSubheader disableSticky>Manual derivatives</ListSubheader>

        <MenuItem onClick={onToggleAnswers}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Show answers"
            secondary="Off keeps the progress and hides every value"
            secondaryTypographyProps={{ variant: "caption" }}
          />
          <Switch
            size="small"
            checked={areAnswersShown}
            inputProps={{ "aria-label": "Show answers" }}
            onClick={(event) => {
              event.stopPropagation();
              onToggleAnswers();
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default SettingsMenu;
